import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Form,
    Input,
    Button,
    Select,
    Switch,
    Card,
    Row,
    Col,
    Spin,
    Space,
    ColorPicker,
    InputNumber
} from 'antd';
import { SaveOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import * as actions from 'store/actions/AuthActions';

const { TextArea } = Input;
const { Option } = Select;

const LinkCategoryForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        loadCategories();
        if (id) {
            loadCategoryData();
        }
    }, [id]);

    const loadCategories = async () => {
        const data = await actions.loadAllLinkCategories();
        // Filter out the current category if editing
        setCategories(data?.filter(cat => cat.id !== id) || []);
    };

    const loadCategoryData = async () => {
        setLoading(true);
        try {
            const data = await actions.fetchLinkCategory(id);
            if (data && !data.response) {
                form.setFieldsValue({
                    ...data,
                    parent_id: data.parent,
                    color: data.color || '#6366f1',
                });
            }
        } catch (err) {
            console.error('Error loading category:', err);
        }
        setLoading(false);
    };

    const onFinish = async (values) => {
        setSubmitting(true);
        try {
            const payload = {
                ...values,
                parent_id: values.parent_id || null,
                color: typeof values.color === 'string' ? values.color : values.color?.toHexString() || '#6366f1',
            };

            if (id) {
                await actions.updateLinkCategory(payload, id);
            } else {
                await actions.addLinkCategory(payload);
            }
            navigate('/links/categories');
        } catch (err) {
            console.error('Error saving category:', err);
        }
        setSubmitting(false);
    };

    const generateSlug = (name) => {
        return name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div style={{ padding: '20px' }}>
            <Card
                title={
                    <Space>
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate('/links/categories')}
                        />
                        {id ? 'Edit Category' : 'Create New Category'}
                    </Space>
                }
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    initialValues={{
                        color: '#6366f1',
                        order: 0,
                        is_active: true,
                    }}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Name"
                                name="name"
                                rules={[{ required: true, message: 'Please enter a name' }]}
                            >
                                <Input
                                    placeholder="Enter category name"
                                    onChange={(e) => {
                                        if (!id) {
                                            form.setFieldsValue({ slug: generateSlug(e.target.value) });
                                        }
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Slug"
                                name="slug"
                                rules={[{ required: true, message: 'Please enter a slug' }]}
                            >
                                <Input placeholder="category-slug" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="Description"
                        name="description"
                    >
                        <TextArea rows={3} placeholder="Category description" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                label="Color"
                                name="color"
                            >
                                <ColorPicker format="hex" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                label="Icon"
                                name="icon"
                            >
                                <Input placeholder="Icon class name (e.g., folder)" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                label="Order"
                                name="order"
                            >
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Parent Category"
                                name="parent_id"
                            >
                                <Select
                                    placeholder="Select parent category (optional)"
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {categories.map(cat => (
                                        <Option key={cat.id} value={cat.id}>{cat.name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Active"
                                name="is_active"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item>
                        <Space>
                            <Button
                                type="primary"
                                htmlType="submit"
                                icon={<SaveOutlined />}
                                loading={submitting}
                            >
                                {id ? 'Update Category' : 'Create Category'}
                            </Button>
                            <Button onClick={() => navigate('/links/categories')}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};

export default LinkCategoryForm;
